import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { createColumnHelper, flexRender, getCoreRowModel, SortingState, useReactTable } from '@tanstack/react-table';

import { AccessModes, Dimensions, PREDECESSOR_LOCATION_LABEL, SortOrder } from 'components/insights/constants';
import CircularProgress from 'components/shared/CircularProgress';
import {
  buildMobileGridTemplateColumns,
  DataTableTheme,
  Table as StyledTable,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderFooterRow,
  TableRow,
} from 'components/shared/DataTable';
import ExpandableArea from 'components/shared/ExpandableArea';
import { buildGridTemplateTracks } from 'components/shared/table/buildGridTemplateColumns';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { simpleDate } from 'lib/date';
import { Analytics, retrieveAnalytics } from 'services/api/insights/analytics';
import { serializeSort } from 'stores/helpers';
import { getNetworkPerformanceAnalyticsFilters, useNetworkPerformanceStore } from 'stores/networkPerformanceStore';
import ArrowUpDownIcon from 'svg/ArrowUpDownIcon';

import NetworkPerformanceApiExport from '../NetworkPerformanceApiExport';

import { METRIC_CONFIGS, NetworkPerformanceMetricDrillKey } from './constants';
import { PATIENT_LIST_CONFIGS } from './patientListConfigs';

type PatientRow = {
  patientName: string;
  episodeUrl: string;
  predecessorLocation: string;
  providerName: string;
  createdDate: string;
  extraValues: Record<string, string>;
};

const columnHelper = createColumnHelper<PatientRow>();

function buildColumns(
  metric: NetworkPerformanceMetricDrillKey,
  predecessorGroupTypeName: string,
  selectedGroupTypeName: string
) {
  const plConfig = PATIENT_LIST_CONFIGS[metric];
  return [
    columnHelper.accessor('patientName', {
      id: Dimensions.PATIENT_NAME,
      header: 'Patient Name',
      cell: (info) => {
        const name = info.getValue();
        const { episodeUrl } = info.row.original;
        if (!name) return '\u2014';
        if (!episodeUrl) return name;
        return (
          <PatientLink href={episodeUrl} target='_blank' rel='noopener noreferrer'>
            {name}
          </PatientLink>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor('predecessorLocation', {
      id: Dimensions.PREDECESSOR_LOCATION,
      header: `Referring ${predecessorGroupTypeName}`,
      cell: (info) => info.getValue() || '\u2014',
      enableSorting: true,
    }),
    columnHelper.accessor('providerName', {
      id: plConfig.patientListGroupDimension,
      header: selectedGroupTypeName,
      cell: (info) => info.getValue() || '\u2014',
      enableSorting: true,
    }),
    columnHelper.accessor('createdDate', {
      id: plConfig.patientListDateDimension ?? Dimensions.DATE,
      header: plConfig.dateColumnHeader,
      cell: (info) => (info.getValue() ? simpleDate(info.getValue()) : '\u2014'),
      enableSorting: true,
    }),
    ...(plConfig.patientListExtraDimensions ?? []).map((extra) =>
      columnHelper.accessor((row) => row.extraValues[extra.dimension] ?? '', {
        id: extra.dimension,
        header: extra.header,
        cell: (info) => {
          const v = info.getValue();
          return v ? extra.format(v) : '\u2014';
        },
        enableSorting: true,
      })
    ),
  ];
}

function getPatientListDimensions(metric: NetworkPerformanceMetricDrillKey) {
  const plConfig = PATIENT_LIST_CONFIGS[metric];
  const dateDim = plConfig.patientListDateDimension ?? Dimensions.DATE;
  const extras = (plConfig.patientListExtraDimensions ?? []).map((e) => e.dimension);
  return [
    Dimensions.PATIENT_NAME,
    Dimensions.EPISODE_URL,
    Dimensions.PREDECESSOR_LOCATION,
    plConfig.patientListGroupDimension,
    dateDim,
    ...extras,
  ];
}

type NetworkPerformancePatientListProps = {
  metric: NetworkPerformanceMetricDrillKey;
  extraFilters?: Record<string, unknown>;
  exportFilename?: string;
};

export default function NetworkPerformancePatientList({
  metric,
  extraFilters,
  exportFilename,
}: NetworkPerformancePatientListProps) {
  const selectedGroupType = useNetworkPerformanceStore((s) => s.selectedGroupType);
  const selectedGroupTypeName = selectedGroupType!.displayName;
  const config = METRIC_CONFIGS[metric];
  const plConfig = PATIENT_LIST_CONFIGS[metric];

  const baseParams = useMemo(
    () => ({
      source: config.source,
      accessModes: [AccessModes.MANAGER],
      dimensions: getPatientListDimensions(metric),
      metrics: [],
      ...(config.reviewType ? { reviewType: config.reviewType } : {}),
    }),
    [config, metric]
  );

  const defaultSortDimension =
    plConfig.patientListDefaultSortId ?? plConfig.patientListDateDimension ?? Dimensions.DATE;

  const exportParams = useMemo(
    () => ({
      ...baseParams,
      sortBy: `${defaultSortDimension} ${SortOrder.DESC}`,
      dimensionDisplayNames: {
        [Dimensions.PREDECESSOR_LOCATION]: `Referring ${PREDECESSOR_LOCATION_LABEL}`,
        [plConfig.patientListGroupDimension]: selectedGroupTypeName,
        [plConfig.patientListDateDimension ?? Dimensions.DATE]: plConfig.dateColumnHeader,
        ...(plConfig.patientListExtraDimensions ?? []).reduce(
          (acc, e) => ({ ...acc, [e.dimension]: e.header }),
          {} as Record<string, string>
        ),
      },
    }),
    [baseParams, defaultSortDimension, plConfig, selectedGroupTypeName]
  );

  const exportExtraFilters = useMemo(() => {
    const reviewFilter = config.reviewType ? { reviewType: config.reviewType } : {};
    return { ...reviewFilter, ...extraFilters };
  }, [config, extraFilters]);

  const filename = exportFilename ?? `post-discharge-${config.exportSlug}-patients`;

  return (
    <Wrapper>
      <Header>
        <Title>{plConfig.patientListTitle ?? 'Patients'}</Title>
        <NetworkPerformanceApiExport params={exportParams} filename={filename} extraFilters={exportExtraFilters} />
      </Header>
      <ExpandableArea mobileFlush>
        <PatientListTable
          metric={metric}
          baseParams={baseParams}
          defaultSortDimension={defaultSortDimension}
          selectedGroupTypeName={selectedGroupTypeName}
          extraFilters={extraFilters}
        />
      </ExpandableArea>
    </Wrapper>
  );
}

function PatientListTable({
  metric,
  baseParams,
  defaultSortDimension,
  selectedGroupTypeName,
  extraFilters,
}: {
  metric: NetworkPerformanceMetricDrillKey;
  baseParams: Record<string, unknown>;
  defaultSortDimension: string;
  selectedGroupTypeName: string;
  extraFilters?: Record<string, unknown>;
}) {
  const plConfig = PATIENT_LIST_CONFIGS[metric];
  const storeFilters = useNetworkPerformanceStore((state) => getNetworkPerformanceAnalyticsFilters(state));
  const baseFilters = useMemo(
    () => (storeFilters ? { ...storeFilters, ...extraFilters } : null),
    [storeFilters, extraFilters]
  );

  const [sort, setSort] = useState<SortingState>([{ id: defaultSortDimension, desc: true }]);

  const queryParams = useMemo(
    () => ({
      ...baseParams,
      sortBy: serializeSort(sort),
      pageSize: 10,
    }),
    [baseParams, sort]
  );

  const query = useInfiniteQuery({
    queryKey: ['network-performance-slideout-patient-list', metric, queryParams, baseFilters],
    queryFn: ({ pageParam }) =>
      retrieveAnalytics(
        pageParam ? { ...queryParams, ...(pageParam as Record<string, unknown>) } : queryParams,
        baseFilters
      ),
    enabled: !!baseFilters,
    placeholderData: keepPreviousData,
    initialPageParam: undefined as Record<string, unknown> | undefined,
    getNextPageParam: (lastPage: Analytics) =>
      (lastPage?.links?.next?.payload as Record<string, unknown> | undefined) ?? undefined,
  });

  const rows = useMemo<PatientRow[]>(() => {
    const pages = query.data?.pages ?? [];
    if (!pages.length) return [];
    const headers = pages[0]?.dimensionHeaders ?? [];
    const idx = (name: string) => headers.findIndex((h) => h.name === name);
    const patientNameIdx = idx(Dimensions.PATIENT_NAME);
    const episodeUrlIdx = idx(Dimensions.EPISODE_URL);
    const predecessorLocationIdx = idx(Dimensions.PREDECESSOR_LOCATION);
    const providerNameIdx = idx(plConfig.patientListGroupDimension);
    const dateIdx = idx(plConfig.patientListDateDimension ?? Dimensions.DATE);
    return pages.flatMap((page) =>
      (page?.data ?? []).map((row): PatientRow => {
        const dimValue = (i: number) => (i >= 0 ? row.dimensionValues[i]?.value : undefined);
        const extras = (plConfig.patientListExtraDimensions ?? []).reduce(
          (acc, extra) => {
            const i = idx(extra.dimension);
            acc[extra.dimension] = String(dimValue(i) ?? '');
            return acc;
          },
          {} as Record<string, string>
        );
        return {
          patientName: String(dimValue(patientNameIdx) ?? ''),
          episodeUrl: String(dimValue(episodeUrlIdx) ?? ''),
          predecessorLocation: String(dimValue(predecessorLocationIdx) ?? ''),
          providerName: String(dimValue(providerNameIdx) ?? ''),
          createdDate: String(dimValue(dateIdx) ?? ''),
          extraValues: extras,
        };
      })
    );
  }, [
    query.data,
    plConfig.patientListGroupDimension,
    plConfig.patientListDateDimension,
    plConfig.patientListExtraDimensions,
  ]);

  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const { customRef, isIntersecting } = useIntersectionObserver({ threshold: 0.2, root: scrollAreaRef.current });

  useEffect(() => {
    if (isIntersecting && query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage();
    }
  }, [isIntersecting, query]);

  const columns = useMemo(
    () => buildColumns(metric, PREDECESSOR_LOCATION_LABEL, selectedGroupTypeName),
    [metric, selectedGroupTypeName]
  );

  const table = useReactTable({
    columns,
    data: rows,
    state: { sorting: sort },
    onSortingChange: (updater) => {
      const next = updater instanceof Function ? updater(sort) : updater;
      setSort(next);
    },
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const showEmpty = !query.isLoading && rows.length === 0;
  const showNextSpinner = query.isFetchingNextPage;

  const visibleColumns = table.getVisibleLeafColumns();
  const tracks = buildGridTemplateTracks(visibleColumns);

  return (
    <ScrollArea ref={scrollAreaRef}>
      <StyledTable
        $theme={DataTableTheme.DEFAULT}
        $freezeFirstColumn
        $gridTemplateColumns={tracks.join(' ')}
        $mobileGridTemplateColumns={
          visibleColumns.length ? buildMobileGridTemplateColumns(tracks, visibleColumns[0].getSize()) : undefined
        }>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderFooterRow key={headerGroup.id} $theme={DataTableTheme.DEFAULT}>
              {headerGroup.headers.map((header) => (
                <TableHeaderCell
                  key={header.id}
                  isSorted={!!header.column.getIsSorted()}
                  isSortable={header.column.getCanSort()}
                  onClick={header.column.getToggleSortingHandler()}
                  width={header.getSize()}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && (
                    <ArrowUpDownIcon
                      direction={header.column.getIsSorted() || 'none'}
                      height={10}
                      width={10}
                      style={{ marginLeft: 8 }}
                    />
                  )}
                </TableHeaderCell>
              ))}
            </TableHeaderFooterRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} width={cell.column.getSize()}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
          <SentinelRow ref={customRef} />
          {showNextSpinner && (
            <TableRow>
              <SpinnerCell colSpan={columns.length}>
                <CircularProgress size={16} thickness={2} />
              </SpinnerCell>
            </TableRow>
          )}
        </TableBody>
      </StyledTable>
      {showEmpty && <EmptyMessage>No rows found</EmptyMessage>}
    </ScrollArea>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
`;

const ScrollArea = styled.div`
  max-height: 520px;
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;

  @media (max-width: 800px) {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-left: none;
    border-right: none;
    border-bottom: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 24px 24px 16px;
`;

const SentinelRow = styled.tr`
  display: block;
  height: 1px;
`;

const SpinnerCell = styled.td`
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  padding: 12px;
  color: ${({ theme }) => theme.colors.blue100};
`;

const Title = styled.h3`
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  color: ${({ theme }) => theme.colors.black100};
`;

const PatientLink = styled.a`
  color: ${({ theme }) => theme.colors.blue100};
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyMessage = styled.div`
  padding: 24px;
  text-align: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black50};
`;
